import { useMemo, useState } from "react";
import { Button } from "@headlessui/react";
import { PlusIcon } from "@heroicons/react/24/solid";
import { UserRole } from "../../../app/userSlice";
import ModalFrame from "../../../components/ModalFrame";
import Tabs, { SubTab } from "../../../components/Tabs";
import AddUserForm from "../../users/editUserByAdmin/AddUserForm";
import AddExistingTeacherForm from "../AddExistingTeacherForm";
import AddExistingStudentsToCourseForm from "./AddExistingStudentsToCourseForm";

export enum AddTeacherSubTabs {
	Existing = "Existing Teacher",
	New = "New Teacher",
}

export enum AddStudentSubTabs {
	Existing = "Existing Student",
	New = "New Student",
}

interface AddUserToCourseProps {
	courseId: number;
	schoolId: number;
	districtId: number;
	isAddingTeacher: boolean;
	seatsRemaining: number;
	showSeatsWarning: boolean;
	setErrorMessage?: (message: string) => void;
}

export default function AddUserToCourseButton(props: AddUserToCourseProps) {
	const [isFormOpen, setIsFormOpen] = useState(false);
	const [hasAutoSwitched, setHasAutoSwitched] = useState(false);
	const [currentTabName, setCurrentTabName] = useState(
		props.isAddingTeacher ? AddTeacherSubTabs.Existing : AddStudentSubTabs.Existing
	);

	const label = useMemo(() => {
		return props.isAddingTeacher ? "Teacher" : "Student";
	}, [props.isAddingTeacher]);

	const initialSubTabs: SubTab[] = useMemo(
		() =>
			props.isAddingTeacher
				? [
						{
							name: AddTeacherSubTabs.Existing,
							current: currentTabName === AddTeacherSubTabs.Existing,
							content: (
								<AddExistingTeacherForm
									courseId={props.courseId}
									onClose={handleCloseForm}
									onSwitchToNewTab={
										!hasAutoSwitched ? handleAutoSwitchTabs : undefined
									}
								/>
							),
						},
						{
							name: AddTeacherSubTabs.New,
							current: currentTabName === AddTeacherSubTabs.New,
							content: (
								<AddUserForm
									selectedRole={UserRole.Proctor}
									saveButtonText="Add Teacher"
									onClose={handleCloseForm}
									hideDistrictSchool={true}
									courseId={props.courseId}
									schoolId={props.schoolId}
									districtId={props.districtId}
								/>
							),
						},
					]
				: [
						{
							name: AddStudentSubTabs.Existing,
							current: currentTabName === AddStudentSubTabs.Existing,
							content: (
								<AddExistingStudentsToCourseForm
									courseId={props.courseId}
									seatsRemaining={props.seatsRemaining}
									showSeatsWarning={props.showSeatsWarning}
									onClose={handleCloseForm}
									onSwitchToNewTab={
										!hasAutoSwitched ? handleAutoSwitchTabs : undefined
									}
									setErrorMessage={props.setErrorMessage}
								/>
							),
						},
						{
							name: AddStudentSubTabs.New,
							current: currentTabName === AddStudentSubTabs.New,
							content:
								props.seatsRemaining < 1 ? (
									<>
										<div className="mt-4 px-4 py-8 bg-gray-50 border border-gray-200 rounded-lg">
											<p className="text-gray-700 text-center">
												There are 0 seats remaining for the school. A new
												student cannot be added to the course, please
												contact the MI Write team to request more seats.
											</p>
										</div>
										<button
											className="btn btn-neutral w-full mt-3"
											onClick={handleCloseForm}>
											Cancel
										</button>
									</>
								) : (
									<AddUserForm
										selectedRole={UserRole.Writer}
										saveButtonText="Add Student"
										onClose={handleCloseForm}
										hideDistrictSchool={true}
										courseId={props.courseId}
										schoolId={props.schoolId}
										districtId={props.districtId}
									/>
								),
						},
					],
		[props, currentTabName, hasAutoSwitched]
	);

	function handleOpenForm() {
		setIsFormOpen(true);
		setCurrentTabName(
			props.isAddingTeacher ? AddTeacherSubTabs.Existing : AddStudentSubTabs.Existing
		);
		setHasAutoSwitched(false);
	}

	function handleCloseForm() {
		setIsFormOpen(false);
	}

	function handleAutoSwitchTabs() {
		setHasAutoSwitched(true);
		setCurrentTabName(props.isAddingTeacher ? AddTeacherSubTabs.New : AddStudentSubTabs.New);
	}

	return (
		<>
			<Button
				id={`add-${label.toLowerCase()}-button`}
				onClick={handleOpenForm}
				className="btn btn-primary">
				<span className="flex items-center">
					<PlusIcon className="size-4 mr-2" /> Add
				</span>
			</Button>

			<ModalFrame
				open={isFormOpen}
				title={`Add ${label} to Course`}
				onClose={handleCloseForm}
				maxWidth="max-w-[750px]"
				overflow="overflow-visible">
				<Tabs
					initialSubTabs={initialSubTabs}
					ariaLabel={`Add ${label} Tabs`}
					containerClass="mt-1.5"
					tabPadding="p-2"
					forceActiveTabName={hasAutoSwitched ? currentTabName : undefined}
				/>
			</ModalFrame>
		</>
	);
}
